import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { MIN_VALIDATIONS_PER_LABEL } from '@/features/datasets/utils/improvementModeReviewGoals';
import { exampleText } from '@/features/datasets/utils/datasetSourceConfig';

export const REVIEW_PLAN_TITLE = 'Review Training Plan';
export const FINISH_AND_TRAIN_LABEL = 'Finish & Train';
export const START_TRAINING_LABEL = 'Start Training';
export const REVIEW_EXAMPLES_LABEL = 'Review all examples in Data Editor';
export const FULL_TRAINING_OVERVIEW_TITLE = 'Full Training Overview';
export const DATA_EDITOR_TITLE = 'Data Editor';
export const DATA_EDITOR_BACK_LABEL = 'Cancel - Back to Review';
export const DATA_EDITOR_SAVE_LABEL = 'Save Changes';
export const PREVIEW_PAGE_SIZE = 20;

export const lowCountTooltip = (noun) =>
    `We recommend providing at least ${MIN_VALIDATIONS_PER_LABEL} examples per ${noun}.`;

const isEntityTask = (taskType) =>
    taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS;

/** Occurrence count from a `get-label-counts` entry (`{ total }` or a bare number). */
export function occurrenceCount(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (value && typeof value === 'object') {
        const total = Number(value.total);
        return Number.isFinite(total) ? total : 0;
    }
    return 0;
}

const namesFrom = (defined, counts) => {
    const ordered = [];
    const seen = new Set();
    (defined || []).forEach((name) => {
        const text = String(name || '').trim();
        if (!text || seen.has(text)) return;
        seen.add(text);
        ordered.push(text);
    });
    Object.keys(counts || {})
        .map((name) => String(name).trim())
        .filter((name) => name && !seen.has(name))
        .sort((a, b) => a.localeCompare(b))
        .forEach((name) => ordered.push(name));
    return ordered;
};

/**
 * Data Overview for Review Training Plan.
 * Classification: one card per label. NER: one card per entity.
 * Summarization and content generation: an example total only.
 * A defined name with zero occurrences still appears. `total` is the sum of
 * occurrence counts (not the number of names). A count under
 * `MIN_VALIDATIONS_PER_LABEL` is a warning, not a blocker.
 */
export function buildReviewOverview({
    taskType,
    labelCounts,
    entityCounts,
    recordCounts,
    definedLabels,
    definedEntities,
} = {}) {
    if (isGenerativeTask(taskType)) {
        const raw = recordCounts && typeof recordCounts === 'object' ? Number(recordCounts.total) : 0;
        return {
            kind: 'examples',
            totalTitle: 'Total Examples',
            total: Number.isFinite(raw) ? raw : 0,
            noun: null,
            items: null,
        };
    }

    const entities = isEntityTask(taskType);
    const counts = entities ? entityCounts : labelCounts;
    const items = namesFrom(entities ? definedEntities : definedLabels, counts).map((name) => {
        const count = occurrenceCount(counts?.[name]);
        return { name, count, warn: count < MIN_VALIDATIONS_PER_LABEL };
    });
    return {
        kind: entities ? 'entities' : 'labels',
        totalTitle: entities ? 'Total Entities' : 'Total Labels',
        total: items.reduce((sum, item) => sum + item.count, 0),
        noun: entities ? 'entity' : 'label',
        items,
    };
}

/** Read-only preview tags. Generative rows have no tag column (`null`). */
export function previewTags(record, kind) {
    if (kind === 'labels') {
        const labels = record?.response?.labels;
        return Array.isArray(labels) ? labels.filter(Boolean) : [];
    }
    if (kind === 'entities') {
        const entities = record?.response?.entities;
        if (!Array.isArray(entities)) return [];
        const names = [];
        const seen = new Set();
        entities.forEach((entity) => {
            const name = String(entity?.label || entity?.type || '').trim();
            if (!name || seen.has(name)) return;
            seen.add(name);
            names.push(name);
        });
        return names;
    }
    return null;
}

export function previewExample(record) {
    return exampleText(record?.request_example) || '';
}
